
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DOMAIN, FEATURE, LoggerFactory } from 'Services/Logger';
import { ConfigProvider } from 'Contexts/Config';
import { FeatureFlagsProvider } from 'Components/FeatureFlag';
import { MenuProvider } from 'Contexts/Menu';
import RemoteConfigProvider from 'Providers/RemoteConfigProvider';
import withErrorBoundary from 'Hocs/withErrorBoundary';
import AppToast from 'Components/ProfileMultiProduct/components/Toast/Toast';
import { RequestError } from 'Components/Errors/RequestError';
import DesignWrapper from 'Utils/DesignWrapper/DesignWrapper';
import InternetConnection from 'Utils/InternetConnection';
import { compose } from 'redux';
import { withDevTools } from 'Hocs/withDevTools';
import { withDesignSystem } from 'Hocs/withDesignSystem';
import { GlobalLoading, GlobalLoadingProvider } from 'Utils/GlobalLoading';
import Router from 'Containers/Router';
import Auth0ProviderWithHistory from 'Utils/auth0/auth0';
import { AxiosInterceptor } from 'Utils/auth0/AxiosInterceptor';
import { getVariable } from 'Utils/env';
import { withLDProvider } from 'Services/LaunchDarkly';
import FlutterWebViewValidation from './FlutterWebViewValidation';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <Auth0ProviderWithHistory>
      <FlutterWebViewValidation>
        <QueryClientProvider client={queryClient}>
          <GlobalLoadingProvider>
            <ConfigProvider>
              <MenuProvider>
                <DesignWrapper>
                  <AxiosInterceptor>
                    <RemoteConfigProvider>
                      <FeatureFlagsProvider>
                        <InternetConnection>
                          <Router />
                          <AppToast />
                          <GlobalLoading />
                        </InternetConnection>
                      </FeatureFlagsProvider>
                    </RemoteConfigProvider>
                  </AxiosInterceptor>
                </DesignWrapper>
              </MenuProvider>
            </ConfigProvider>
          </GlobalLoadingProvider>
        </QueryClientProvider>
      </FlutterWebViewValidation>
    </Auth0ProviderWithHistory>
  );
}

export default compose(
  withDesignSystem,
  withErrorBoundary({
    loggingDomain: DOMAIN.KUESKI,
    loggingFeature: FEATURE.APP,
    onErrorElement: <RequestError />,
  }),
  withDevTools,
  withLDProvider({
    appName: 'Aurora',
    launchDarklyToken: getVariable('LAUNCHDARKLY_CLIENT'),
    logger: LoggerFactory.getLogger(DOMAIN.WEB),
    version: getVariable('APP_VERSION'),
  }),
)(App);
